import { AxiosRequestConfig } from 'axios';

type Environment = 'local' | 'qa' | 'uat' | 'prod';

const getEnvironment = (): Environment => {
  const mode = import.meta.env.MODE || 'local';

  if (!['local', 'qa', 'uat', 'prod'].includes(mode)) {
    console.warn(`Unexpected environment: ${mode}. Defaulting to 'local'`);
    return 'local';
  }

  return mode as Environment;
};

interface ServiceBaseUrls {
  questionEditor: string;
  orderApi: string;
  userApi: string;
}

const getServiceBaseUrls = (): ServiceBaseUrls => {
  return {
    questionEditor: (import.meta.env.VITE_APP_QE_API_BASE_URL as string) || 'http://localhost:7030/api',
    orderApi: (import.meta.env.VITE_APP_ORDER_API_BASE_URL as string) || 'https://localhost:7061/api',
    userApi: (import.meta.env.VITE_APP_USER_API_BASE_URL as string) || 'https://localhost:7246/api'
  };
};

const orderApiScope = import.meta.env.VITE_APP_ORDER_API_SCOPE as string;
const userApiScope = import.meta.env.VITE_APP_USER_API_SCOPE as string;

interface ApiEndpoint {
  path: string;
  service: 'questionEditor' | 'orderApi' | 'userApi';
  needsApiKey?: boolean;
  keyType?: 'LIST' | 'READ';
  scope?: string;
}

const endpoints: Record<string, ApiEndpoint> = {
  // Question Editor Service Endpoints
  questionEditorList: {
    path: '/QuestionEditorListHttpTrigger',
    service: 'questionEditor',
    needsApiKey: true,
    keyType: 'LIST'
  },
  questionEditorRead: {
    path: '/QuestionEditorReadHttpTrigger',
    service: 'questionEditor',
    needsApiKey: true,
    keyType: 'READ'
  },
  // Order Service Endpoints
  orderItems: {
    path: '/orderitems',
    service: 'orderApi',
    scope: orderApiScope
  },
  orderItem: {
    path: '/orderitems/:id',
    service: 'orderApi',
    scope: orderApiScope
  },
  orderItemRegenerateReport: {
    path: '/orderitems/:id/regenerate-report',
    service: 'orderApi',
    scope: orderApiScope
  },
  orderItemEditLease: {
    path: '/orderitems/:id/edit-lease',
    service: 'orderApi',
    scope: orderApiScope
  },
  orderItemEditLeaseHeartbeat: {
    path: '/orderitems/:id/edit-lease/:leaseId/heartbeat',
    service: 'orderApi',
    scope: orderApiScope
  },
  orderItemEditLeaseRelease: {
    path: '/orderitems/:id/edit-lease/:leaseId',
    service: 'orderApi',
    scope: orderApiScope
  },
  orderItemAttachmentFiles: {
    path: '/orderitems/:id/attachments?fileType=:fileType',
    service: 'orderApi',
    scope: orderApiScope
  },
  orderItemAttachments: {
    path: '/orderitems/:id/attachments',
    service: 'orderApi',
    scope: orderApiScope
  },
  customerUploadedPlans: {
    path: '/orderitems/:id/attachments/customer-uploaded-plans',
    service: 'orderApi',
    scope: orderApiScope
  },
  attachmentUrl: {
    path: '/orderitems/attachmentUrl?attachmentName=:attachmentName',
    service: 'orderApi',
    scope: orderApiScope
  },
  comments: {
    path: '/orderitems/:id/comments',
    service: 'orderApi',
    scope: orderApiScope
  },
  commentsWithStatus: {
    path: '/orderitems/:id/comments/with-status',
    service: 'orderApi',
    scope: orderApiScope
  },
  orderItemsExport: {
    path: '/orderitems/export',
    service: 'orderApi',
    scope: orderApiScope
  },
  // User Service Endpoints
  userProfile: {
    path: '/users/:id/profile',
    service: 'userApi',
    scope: userApiScope
  }
};

const generateApiEndpoints = () => {
  const serviceUrls = getServiceBaseUrls();
  const builtEndpoints: Record<string, { url: string; scope?: string }> = {};

  Object.entries(endpoints).forEach(([name, config]) => {
    const baseUrl = serviceUrls[config.service];

    if (!baseUrl) {
      console.warn(`Base URL for ${config.service} not found in environment variables.`);
      return;
    }

    let url = `${baseUrl}${config.path}`;

    if (config.needsApiKey && config.keyType) {
      const keyEnvName = `VITE_APP_QE_API_${config.keyType}_KEY`;
      const apiKey = import.meta.env[keyEnvName];

      if (apiKey) {
        url += `?code=${apiKey}`;
      } else {
        // Don't warn in local development if keys are intentionally blank
        if (getEnvironment() !== 'local') {
          console.warn(`API key ${keyEnvName} not found in environment variables.`);
        }
      }
    }

    builtEndpoints[name] = { url, scope: config.scope };
  });

  return builtEndpoints;
};

const apiEndpoints = generateApiEndpoints();

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  scope?: string;
}
export const apiEndpoint = (
  endpoint: string,
  params: Record<string, string | number> = {}
): { url: string; config: CustomAxiosRequestConfig } => {
  const { url, scope } = apiEndpoints[endpoint];
  if (!url) {
    throw new Error(`Endpoint ${endpoint} not found`);
  }
  let finalUrl = url;
  Object.entries(params).forEach(([key, value]) => {
    finalUrl = finalUrl.replace(`:${key}`, String(value));
  });
  return { url: finalUrl, config: { scope } };
};
